import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Badge from "@/v2-backoffice-client/components/badge"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import TruncatedList from "@/v2-backoffice-client/components/truncatedList"
import Table, {
  createColumns,
  type TableProps,
  useTableSortUrl,
} from "@/v2-backoffice-client/components/table"
import ActionMenu from "@/v2-backoffice-client/components/actionMenu"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import { buildMixpanelUserActivityUrl } from "@/v2-backoffice-client/user/view/user.view.utils"
import {
  FENERUM_ACCOUNT_BASE_URL,
  HUBSPOT_BASE_URL,
} from "@/v2-backoffice-client/constants"
import { useUsageProfilesQuery } from "@/v2-backoffice-client/usage/usage.query"
import type { UsageProfileFragment } from "@/v2-backoffice-client/usage/__types__/usage.fragment"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import UsageScoreBadge from "@/v2-backoffice-client/usage/UsageScoreBadge"
import UsageSparkline from "@/v2-backoffice-client/usage/UsageSparkline"
import UsageDayStrip from "@/v2-backoffice-client/usage/UsageDayStrip"
import UsageAreaBar from "@/v2-backoffice-client/usage/UsageAreaBar"
import UsageAppSplitBar from "@/v2-backoffice-client/usage/UsageAppSplitBar"
import {
  APP_CODE_FILTER_OPTIONS,
  APP_MIX_TOOLTIP,
  formatAppCode,
  getScoreLevel,
  getTrendLevel,
  SCORE_LEVEL_FILTER_OPTIONS,
  SCORE_LEVEL_LABELS,
  SCORE_TOOLTIP,
  TREND_LABELS,
  TREND_LEVEL_FILTER_OPTIONS,
  TREND_TOOLTIP,
  type ScoreLevel,
} from "@/v2-backoffice-client/usage/usage.constants"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import LicenseGoverningBadge from "@/v2-backoffice-client/license/LicenseGoverningBadge"
import {
  useLicenseGoverningStatuses,
  type LicenseStatusFilterOption,
} from "@/v2-backoffice-client/license/license.statuses.hooks"
import {
  getLicenseGoverningStatusLabel,
  pickGoverningLicenseStatusCode,
} from "@/v2-backoffice-client/license/license.utils"

// One signal row = a synced usage profile; identity (name/email/customers)
// is joined server-side by the profiles proc, since profiles span every
// ever-active user (a client-side join against a paged users list dropped
// names for users beyond the first page).
export type UsageSignalRow = UsageProfileFragment & {
  UserName: string
  CustomerNames: string[]
  /**
   * The governing license status across the user's customers (0 = no
   * license held); null when no customer link carries one — so "the
   * trials" can be filtered out of every synced user.
   */
  LicenseStatusCode: number | null
}

const COLUMN_SIZING = {
  EngagementScore: 130,
  UserName: 175,
  LicenseStatusCode: 130,
  // Wide enough for the level text in the badge ("↓ −83% Falling hard").
  WeightedTrendPct: 150,
  Daily28d: 130,
  ActiveDaysStrip: 130,
  TopAreas: 190,
  Apps: 150,
  LastActiveAt: 120,
  Automation: 110,
}

// Filtering is client-side (the query loads every synced profile), so the
// filter configs below run through TanStack's filterFns against getValue.
const COLUMNS = createColumns<UsageSignalRow>({
  enableSorting: true,
  enableHiding: true,
  columns: {
    EngagementScore: {
      id: "EngagementScore",
      label: "Score",
      description: SCORE_TOOLTIP,
      getValue: (row) => row.EngagementScore ?? -1,
      filter: { type: "number-range", placeholder: "0–100" },
      renderCell({ row }) {
        return <UsageScoreBadge score={row.original.EngagementScore} />
      },
      exportValue({ row }) {
        return row.original.EngagementScore ?? ""
      },
      summaryValue({ row }) {
        return row.original.EngagementScore ?? null
      },
    },
    // Virtual column (never rendered — the score badge already shows the
    // level) powering the toolbar quick filter AND "group by score level".
    // Same thresholds as the badges (getScoreLevel), so "Healthy" filters
    // exactly the rows whose badge reads Healthy.
    ScoreLevel: {
      id: "ScoreLevel",
      label: "Score level",
      description: SCORE_TOOLTIP,
      virtual: true,
      enableSorting: false,
      getValue: (row) => getScoreLevel(row.EngagementScore),
      enableGrouping: true,
      renderGroupLabel: (value) =>
        SCORE_LEVEL_LABELS[value as ScoreLevel] ?? String(value),
      filter: {
        type: "multi-select",
        placeholder: "Any level",
        toolbar: true,
        options: SCORE_LEVEL_FILTER_OPTIONS,
      },
      exportValue({ row }) {
        return SCORE_LEVEL_LABELS[getScoreLevel(row.original.EngagementScore)]
      },
    },
    // Same idea for the trend: quick threshold buckets over the 28d trend,
    // displayed inside the Trend column's badge instead of a column of its
    // own.
    TrendLevel: {
      id: "TrendLevel",
      label: "Trend level",
      description: TREND_TOOLTIP,
      virtual: true,
      enableSorting: false,
      getValue: (row) => getTrendLevel(row.WeightedTrendPct),
      enableGrouping: true,
      renderGroupLabel: (value) =>
        TREND_LABELS[value as ReturnType<typeof getTrendLevel>] ??
        String(value),
      filter: {
        type: "multi-select",
        placeholder: "Any trend",
        toolbar: true,
        options: TREND_LEVEL_FILTER_OPTIONS,
      },
      exportValue({ row }) {
        return TREND_LABELS[getTrendLevel(row.original.WeightedTrendPct)]
      },
    },
    UserName: {
      id: "UserName",
      label: "User",
      to: ({ row }) => `?v=user:${row.original.UserId}`,
    },
    CustomerNames: {
      id: "CustomerNames",
      label: "Customer(s)",
      description:
        "Customer(s) whose licensed seats the user occupies. Searching and " +
        "filtering match every linked customer; grouping buckets by the " +
        "first one.",
      enableGrouping: true,
      // Filter/sort read every linked customer; grouping keeps bucketing by
      // the lead customer so multi-customer users don't fragment the groups.
      getValue: (row) => row.CustomerNames.join(", "),
      getGroupingValue: (row) => row.CustomerNames[0] ?? "",
      filter: { type: "text", placeholder: "Customer name…" },
      renderCell({ row }) {
        return (
          <TruncatedList
            items={row.original.Customers.map((customer) => ({
              label: customer.Name,
              to: `?v=customer:${customer.ExternalCustomerId}`,
            }))}
          />
        )
      },
      exportValue({ row }) {
        return row.original.CustomerNames.join(", ")
      },
    },
    WeightedTrendPct: {
      id: "WeightedTrendPct",
      label: "Trend",
      description: TREND_TOOLTIP,
      getValue: (row) => row.WeightedTrendPct ?? 0,
      filter: { type: "number-range", placeholder: "% vs prev 28 d" },
      renderCell({ row }) {
        // showLevel: badge reads like the score badge — "↓ −83% Falling hard".
        return (
          <UsageTrendBadge trendPct={row.original.WeightedTrendPct} showLevel />
        )
      },
      exportValue({ row }) {
        return row.original.WeightedTrendPct ?? ""
      },
      summaryValue({ row }) {
        return row.original.WeightedTrendPct ?? null
      },
    },
    Daily28d: {
      id: "Daily28d",
      label: "Activity (28d)",
      description:
        "Weighted activity per day over the last 28 days, oldest to newest " +
        "— the shape of the user's month at a glance.",
      enableSorting: false,
      renderCell({ row }) {
        return <UsageSparkline series={row.original.Daily28d} />
      },
      exportValue({ row }) {
        return row.original.WeightedEvents28d ?? ""
      },
    },
    ActiveDaysStrip: {
      id: "ActiveDaysStrip",
      label: "Active days",
      description:
        "Which of the last 28 days had any tracked activity (oldest to " +
        "newest) — gaps are quiet days. Filter and sort use the day count.",
      getValue: (row) => row.ActiveDays28d,
      filter: { type: "number-range", placeholder: "0–28" },
      renderCell({ row }) {
        return <UsageDayStrip series={row.original.Daily28d} />
      },
      exportValue({ row }) {
        return row.original.ActiveDays28d
      },
    },
    TopAreas: {
      id: "TopAreas",
      label: "Top areas",
      description:
        "Where the activity concentrates: the top feature areas by " +
        "weighted volume in the last 28 days — the user's interest profile " +
        "and the natural demo/upsell angle.",
      enableSorting: false,
      renderCell({ row }) {
        return <UsageAreaBar areas={row.original.TopAreas28d} />
      },
      exportValue({ row }) {
        return row.original.TopAreas28d.map((area) => area.Area).join(", ")
      },
    },
    Apps: {
      id: "Apps",
      label: "Apps",
      description: `${APP_MIX_TOOLTIP} Filtering and sorting use the dominant app.`,
      enableSorting: false,
      defaultSize: 150,
      // Sort/filter on the dominant app so "who is on v1" is one click —
      // in the toolbar next to score and trend, since which product a user
      // lives in is the same kind of first-cut question.
      getValue: (row) => row.AppMix[0]?.App ?? "",
      filter: {
        type: "multi-select",
        placeholder: "Any app",
        toolbar: true,
        options: APP_CODE_FILTER_OPTIONS,
      },
      renderCell({ row }) {
        return <UsageAppSplitBar appMix={row.original.AppMix} />
      },
      exportValue({ row }) {
        return row.original.AppMix.map(
          (app) => `${formatAppCode(app.App)} ${Math.round(app.Weighted28d)}`,
        ).join(" · ")
      },
    },
    LastActiveAt: {
      id: "LastActiveAt",
      label: "Last active",
      description:
        "When the user's newest tracked action happened, in either product.",
      filter: { type: "date-filter" },
      renderCell({ row }) {
        return <RelativeDate date={row.original.LastActiveAt} />
      },
      exportValue({ row }) {
        return formatDateString(row.original.LastActiveAt)
      },
    },
    Automation: {
      id: "Automation",
      label: "Automation",
      description:
        "Machine-like usage detection: hours in the last 28 days with " +
        "bot-like rate and cadence. Suspects are API-license conversation " +
        "candidates — hover a badge for the evidence.",
      // Boolean value: the boolean filterFn compares strictly, and
      // true/false still sort suspects together.
      getValue: (row) => row.AutomationSuspect,
      filter: { type: "boolean" },
      renderCell({ row }) {
        const profile = row.original
        if (!profile.AutomationSuspect) return null
        const gap =
          profile.MedianPeakGapSeconds != null
            ? ` at a median ${profile.MedianPeakGapSeconds.toFixed(0)}s cadence`
            : ""
        return (
          <Badge
            variant="warning"
            tooltip={
              `${profile.SuspectHours28d ?? 0} hour(s) in the last 28 days with ` +
              `machine-like usage (peak ${formatNumber(profile.PeakEventsPerHour28d)} events/h${gap}) ` +
              `— candidate for an API-license conversation.`
            }
          >
            Suspected
          </Badge>
        )
      },
      exportValue({ row }) {
        return row.original.AutomationSuspect ? "suspected" : ""
      },
    },
    WeightedEvents28d: {
      id: "WeightedEvents28d",
      label: "Weighted (28d)",
      description:
        "Action-weighted activity in the last 28 days: heavy actions " +
        "(exports, reports, saves) count more than clicks, capped per day " +
        "so one wild hour can't dominate. The measure behind score, trend " +
        "and the charts.",
      defaultHidden: true,
      getValue: (row) => row.WeightedEvents28d ?? 0,
      filter: { type: "number-range" },
      renderCell({ row }) {
        return (
          <span className="tabular-nums">
            {row.original.WeightedEvents28d == null
              ? "—"
              : formatNumber(Math.round(row.original.WeightedEvents28d))}
          </span>
        )
      },
      summaryValue({ row }) {
        return row.original.WeightedEvents28d ?? null
      },
    },
    Events28d: {
      id: "Events28d",
      label: "Events (28d)",
      description:
        "Raw tracked UI events in the last 28 days, unweighted — a click " +
        "counts the same as an export.",
      defaultHidden: true,
      getValue: (row) => row.Events28d,
      filter: { type: "number-range" },
      renderCell({ row }) {
        return (
          <span className="tabular-nums">
            {formatNumber(row.original.Events28d)}
          </span>
        )
      },
    },
    ActiveDays28d: {
      id: "ActiveDays28d",
      label: "Active days (n)",
      description:
        "Distinct days with any tracked activity in the last 28 days.",
      defaultHidden: true,
      getValue: (row) => row.ActiveDays28d,
      renderCell({ row }) {
        return (
          <span className="tabular-nums">{row.original.ActiveDays28d}/28</span>
        )
      },
    },
    PeakEventsPerHour28d: {
      id: "PeakEventsPerHour28d",
      label: "Peak events/h",
      description:
        "Most events inside any single hour in the last 28 days — the raw " +
        "material of the automation signal.",
      defaultHidden: true,
      getValue: (row) => row.PeakEventsPerHour28d,
    },
    BurstPct28d: {
      id: "BurstPct28d",
      label: "Burst",
      description:
        "Share of the 28-day activity that happened inside the single " +
        "busiest hour — high means bot-like bursts rather than human use.",
      defaultHidden: true,
      getValue: (row) => row.BurstPct28d ?? 0,
      exportValue({ row }) {
        return row.original.BurstPct28d ?? ""
      },
      summaryValue({ row }) {
        return row.original.BurstPct28d ?? null
      },
    },
    Events7d: {
      id: "Events7d",
      label: "Events (7d)",
      description: "Raw tracked UI events in the last 7 days.",
      defaultHidden: true,
      getValue: (row) => row.Events7d,
    },
    EventsPrev28d: {
      id: "EventsPrev28d",
      label: "Events (prev 28d)",
      description:
        "Raw events in the 28 days BEFORE the current window — the " +
        "baseline the trend compares against.",
      defaultHidden: true,
      getValue: (row) => row.EventsPrev28d,
    },
    Email: {
      id: "Email",
      label: "Email",
      defaultHidden: true,
      getValue: (row) => row.Email ?? "",
    },
    UserId: {
      id: "UserId",
      label: "Id",
      defaultSize: 50,
      defaultHidden: true,
    },
  },
})

/** The column the License column follows: the customer it is the license of. */
const LICENSE_COLUMN_ANCHOR = "CustomerNames"

type LicenseColumnInput = {
  /** License-status options (statuses + "No license") by display name. */
  licenseStatusOptions: LicenseStatusFilterOption[]
  /** Status display names from the getLicenseStatuses lookup. */
  licenseStatusNamesByCode: Map<number, string>
}

// The license the user's customer is on — "the trials" as a toolbar facet
// over every synced user, which the Portfolio's license facet only offered
// per customer. Built apart from COLUMNS because its options come from the
// status lookup; filter and group read the status NAME, since the table
// filters client-side against getValue.
function buildLicenseColumn(input: LicenseColumnInput) {
  const { licenseStatusOptions, licenseStatusNamesByCode } = input
  return createColumns<UsageSignalRow>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      LicenseStatusCode: {
        id: "LicenseStatusCode",
        label: "License",
        description:
          "The governing license status of the user's customer (a trial " +
          "outranks everything; across several customers, the highest-" +
          "ranked one). Blank when the customer link carries no license facts.",
        enableGrouping: true,
        getValue: (row) =>
          getLicenseGoverningStatusLabel(
            row.LicenseStatusCode,
            licenseStatusNamesByCode,
          ),
        filter: {
          type: "multi-select",
          placeholder: "Any license",
          toolbar: true,
          options: licenseStatusOptions,
        },
        renderCell({ row }) {
          const code = row.original.LicenseStatusCode
          if (code == null) return null
          return (
            <LicenseGoverningBadge
              license={{ LicenseStatusCode: code }}
              statusName={licenseStatusNamesByCode.get(code)}
            />
          )
        },
        exportValue({ row }) {
          return getLicenseGoverningStatusLabel(
            row.original.LicenseStatusCode,
            licenseStatusNamesByCode,
          )
        },
      },
    },
  })
}

// Weakest engagement first: churn candidates surface on top; the operator
// can re-sort by the Automation column to hunt scripted usage instead.
const INITIAL_SORTING = [{ id: "EngagementScore", desc: false }]

const TABLE_ID = "usage-table"

export type UsageTableProps = Omit<
  TableProps<UsageSignalRow>,
  "data" | "columns"
>

function UsageTable(props: UsageTableProps) {
  const { className, ...tableProps } = props
  const opener = useAppViewSheetOpener()

  const { data, loading, error, refetch } = useUsageProfilesQuery()

  // The License column slots in after Customer(s), where it reads as one of
  // the customer facts. Rebuilt when the status lookup lands, so the facet
  // lists the real names, not just "No license".
  const { namesByCode: licenseStatusNamesByCode, labelOptions } =
    useLicenseGoverningStatuses()
  const columns = useMemo(() => {
    const licenseColumns = buildLicenseColumn({
      licenseStatusOptions: labelOptions,
      licenseStatusNamesByCode,
    })
    const anchor =
      COLUMNS.findIndex((column) => column.id === LICENSE_COLUMN_ANCHOR) + 1
    return [
      ...COLUMNS.slice(0, anchor),
      ...licenseColumns,
      ...COLUMNS.slice(anchor),
    ]
  }, [labelOptions, licenseStatusNamesByCode])

  const rows = useMemo<UsageSignalRow[]>(() => {
    return (data?.getUsageProfiles ?? []).map((profile) => ({
      ...profile,
      UserName:
        profile.FirstName || profile.LastName
          ? formatUserName(profile.FirstName, profile.LastName)
          : (profile.Email ?? `User #${profile.UserId}`),
      CustomerNames: profile.Customers.map((customer) => customer.Name),
      LicenseStatusCode: pickGoverningLicenseStatusCode(
        profile.Customers.map((customer) => customer.LicenseStatusCode),
      ),
    }))
  }, [data])

  // Bookmarkable sort: the primary sort column round-trips through
  // `?usage-table-sort=…`, so a shared/reloaded URL restores the view.
  const sortUrl = useTableSortUrl({
    tableId: TABLE_ID,
    initialSorting: INITIAL_SORTING,
  })

  return (
    <Table<UsageSignalRow>
      className={cn("usage-table", className)}
      tableId={TABLE_ID}
      data={rows}
      columns={columns}
      initialColumnSizing={COLUMN_SIZING}
      {...sortUrl}
      enableGrouping
      enableInfiniteScroll
      getRowId={(row) => String(row.UserId)}
      getHighlightedRowId={(row) => {
        return opener.getIsActive("user", row.original.UserId)
      }}
      {...tableProps}
    >
      <Table.Toolbar>
        <div className="flex-1" />
        {/* Global search (no column pin): matches user, email, customer —
            the standardized behavior across every user-shaped table. */}
        <Table.Search placeholder="Search user, email or customer…" />
        <Table.ToolbarFilters />
        <Separator orientation="vertical" />
        <Table.ToolbarMenu onRefresh={refetch} exportFileName="usage-signals" />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<UsageSignalRow> column="UserName">
        {(row) => <UsageTableRowToolbar row={row} />}
      </Table.RowToolbar>

      <Table.Content
        countLabel="users"
        isLoading={loading}
        error={error}
        renderEmpty={
          <div className="text-sm text-muted-foreground text-center py-10">
            No usage profiles synced yet. Profiles appear after the daily
            v2-usage-sync run.
          </div>
        }
      />
    </Table>
  )
}

type UsageTableRowToolbarProps = {
  row: UsageSignalRow
}

// Signals are observations; acting on them happens in the external tools.
// HubSpot deep-links go via the customer: UsageProfile rows don't carry
// the user's contact link (acc.UserContacts), and the company page is
// where account follow-up lives. The user's own contact is one click
// away via the user panel.
function UsageTableRowToolbar(props: UsageTableRowToolbarProps) {
  const { row } = props

  return (
    <ActionMenu>
      <ActionMenu.Trigger />

      <ActionMenu.Content>
        {row.Customers.map((customer) => (
          <ActionMenu.LinkItem
            key={customer.ExternalCustomerId}
            iconSrc="hubspot"
            external
            to={`${HUBSPOT_BASE_URL}/${customer.ExternalCustomerId}`}
          >
            {row.Customers.length > 1
              ? `Open ${customer.Name} in HubSpot`
              : "Open in HubSpot"}
          </ActionMenu.LinkItem>
        ))}

        {row.Customers.filter((customer) => customer.BillingAccountUuid).map(
          (customer) => (
            <ActionMenu.LinkItem
              key={customer.ExternalCustomerId}
              iconSrc="bank"
              external
              to={`${FENERUM_ACCOUNT_BASE_URL}/${customer.BillingAccountUuid}/`}
            >
              {row.Customers.length > 1
                ? `Open ${customer.Name} in Fenerum`
                : "Open in Fenerum"}
            </ActionMenu.LinkItem>
          ),
        )}

        <ActionMenu.LinkItem
          iconSrc="mixpanel"
          disabled={!row.Email}
          external
          to={row.Email ? buildMixpanelUserActivityUrl(row.Email) : ""}
        >
          Open in Mixpanel
        </ActionMenu.LinkItem>
      </ActionMenu.Content>
    </ActionMenu>
  )
}

export default UsageTable
